import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { KeyRound, Loader2, CheckCircle, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { ATLAS_LOGO_URL, ATLAS_NAME } from '@/lib/brand';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error('Missing reset token');
      return;
    }
    if (pw.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (pw !== pw2) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pw });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <button onClick={() => navigate('/')} className="inline-flex flex-col items-center gap-3 group" data-testid="reset-brand">
            <div className="w-16 h-16 rounded-lg bg-white border border-[#EBE7E0] flex items-center justify-center overflow-hidden shadow-sm">
              <img src={ATLAS_LOGO_URL} alt="" className="w-full h-full object-contain" />
            </div>
            <div>
              <h1 className="font-serif-display text-2xl text-[#14213D] tracking-tight">{ATLAS_NAME}</h1>
              <p className="text-[11px] uppercase tracking-[0.18em] text-[#5A6577] mt-1">Editorial Archive</p>
            </div>
          </button>
        </div>

        <Card className="p-6 border border-[#EBE7E0]" data-testid="reset-password-card">
          {done ? (
            <div className="text-center py-4" data-testid="reset-done-state">
              <div className="w-12 h-12 mx-auto rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mb-4">
                <CheckCircle className="h-6 w-6 text-emerald-600" />
              </div>
              <h2 className="font-serif-display text-xl text-[#14213D] mb-2">Password reset</h2>
              <p className="text-sm text-[#5A6577] mb-6">Your password has been updated. You can now sign in with your new password.</p>
              <Button
                onClick={() => navigate('/login')}
                className="w-full bg-[#14213D] hover:bg-[#0F1A2E] text-[#FDFBF7]"
                data-testid="go-to-login-btn"
              >
                Go to Sign In
              </Button>
            </div>
          ) : (
            <>
              <div className="mb-5">
                <h2 className="font-serif-display text-xl text-[#14213D]">Choose a new password</h2>
                <p className="text-sm text-[#5A6577] mt-1">Must be at least 6 characters.</p>
              </div>
              {!token && (
                <div className="mb-4 p-3 rounded border border-red-200 bg-red-50 text-xs text-red-700" data-testid="reset-missing-token">
                  This reset link is invalid. Request a new one from the
                  &quot;Forgot password&quot; page.
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-sm text-[#5A6577] mb-1 block">New password</label>
                  <Input
                    type="password"
                    required
                    minLength={6}
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    data-testid="reset-pw-input"
                  />
                </div>
                <div>
                  <label className="text-sm text-[#5A6577] mb-1 block">Confirm new password</label>
                  <Input
                    type="password"
                    required
                    minLength={6}
                    value={pw2}
                    onChange={(e) => setPw2(e.target.value)}
                    data-testid="reset-pw2-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading || !token}
                  className="w-full bg-[#14213D] hover:bg-[#0F1A2E] text-[#FDFBF7]"
                  data-testid="reset-submit-btn"
                >
                  {loading ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Resetting…</>
                  ) : (
                    <><KeyRound className="h-4 w-4 mr-2" /> Reset password</>
                  )}
                </Button>
              </form>
              <div className="mt-5 text-center">
                <Link
                  to="/login"
                  className="text-xs text-[#5A6577] hover:text-[#14213D] inline-flex items-center gap-1"
                  data-testid="back-to-login-link"
                >
                  <ArrowLeft className="h-3 w-3" /> Back to sign in
                </Link>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
